/* ============================================================
   DW Arcade — Detail Dash (skill game #3). A top-down arcade dodger:
   hold your DW car in its lane while ONCOMING traffic barrels straight down
   the highway at you. Weave through the gaps, snatch wax pickups, and chain
   near-misses for a combo multiplier. Score maps to a discount tier (5-15% off) at claim time.
   Analog steering (the physical cabinet wheel).

   ONCOMING, not overtaking. NPC cars close on you at (your speed + their speed),
   grow with perspective as they approach, and face the player (headlights at you).
   They are solid to each other — guaranteed spawn spacing plus per-frame
   separation means cars never clip or merge. Ram one and it wrecks, spins out,
   and is flung past you.

   SPRITE-DRIVEN. The hero car is a real pixel-art sprite set, swapped live by
   STEER direction and DAMAGE tier, with a two-frame crash on the final wreck:

     direction : forward | left | right        (lean while steering)
     damage    : 0 clean · 1 first · 2 second   (= hits taken; 3 lives)
     crash     : crash-burst (impact) -> crash  (settled wreck)

   Assets are handed in by Liquid via window.DwArcadeAssets.racing.car
   (versioned Shopify URLs). If that's absent, URLs are derived from this
   script's own /assets/ path using the convention `dw-car-<key>.png`
   (e.g. dw-car-forward-0.png, dw-car-crash-burst.png). Until an image loads,
   the car falls back to a clean vector van so the game is always playable.

   DROP-IN ART (Sean is building these): add keys to the manifest and they're
   used automatically — `road` / `scenery` background images, and an
   `obstacles` map ({ wax }) to replace the remaining vector props.

   Contract extras (see dw-game-pacman.js / dw-game-foamstack.js):
     steer(v)   — analog steering axis, v in [-1, 1]
     controls   — { steer:true, showLives:true }
   ============================================================ */
(function () {
  'use strict';

  /* ---- brand palette (canvas can't read CSS vars; these mirror dw-base.css) ---- */
  var C = {
    night1: '#1F1238', night2: '#2D1B4E',          // purple-900 / purple-800 (arcade sky)
    grass: '#0E2A16', grassLt: '#13361D',           // shoulder green (derived dark)
    road: '#23262E', roadLt: '#2E3138', seam: '#191B20', // asphalt (ink-700 family)
    edge: '#F7F8FA', dash: '#D4A437',               // ink-50 lines / gold-500 dashes
    curb: '#191227', reflector: 'rgba(212,164,55,0.78)', // calm dark curb / single-colour amber reflector (no strobe)
    green: '#22B84A', greenDk: '#1A9E3E',           // green-500 / green-600 (player + UI)
    glass: '#9FE3FF', head: '#FFE8A6',              // windshield / headlight (derived)
    traffic1: '#3A2563', traffic2: '#D52B3A',       // purple-700 / red-600 (other cars)
    cone: '#FF9E3D', grime: '#14161A',              // cone orange / ink-900 grime
    wax: '#D4A437', waxDk: '#1F1238',               // gold-500 / purple-900
    text: '#F7F8FA', dim: '#B8BCC4', urgent: '#E63946', spark: '#FFE8A6'
  };

  var LANES = 4, STEER_SPEED = 460, START_LIVES = 3, CRASH_DUR = 1.4;
  var BASE_SPEED = 300, MAX_SPEED = 660;
  var SPRITE_AR = 1132 / 944;        // source frame aspect (h / w)
  var BODY_FRAC = 0.60;              // car body fills ~60% of frame width
  var TAU = Math.PI * 2;

  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
  function rand(a, b) { return a + Math.random() * (b - a); }

  /* ---- asset resolution (shared across instances) ---- */
  var SCRIPT_BASE = (function () {
    try {
      var s = document.currentScript;
      if (!s) {
        var all = document.getElementsByTagName('script');
        for (var i = all.length - 1; i >= 0; i--) {
          if (all[i].src && all[i].src.indexOf('dw-game-racing') !== -1) { s = all[i]; break; }
        }
      }
      if (s && s.src) return s.src.replace(/[^/?#]*([?#].*)?$/, '');
    } catch (e) {}
    return '';
  })();

  function manifest() {
    return (window.DwArcadeAssets && window.DwArcadeAssets.racing) || null;
  }
  function carUrl(key) {
    var m = manifest();
    if (m && m.car && m.car[key]) return m.car[key];
    return SCRIPT_BASE ? (SCRIPT_BASE + 'dw-car-' + key + '.png') : '';
  }

  var IMG_CACHE = {};
  function getImg(url) {
    if (!url) return null;
    if (IMG_CACHE[url]) return IMG_CACHE[url];
    var im = new Image();
    im._ready = false;
    im.onload = function () { im._ready = true; };
    im.onerror = function () { im._failed = true; };
    im.src = url;
    IMG_CACHE[url] = im;
    return im;
  }

  var CAR_KEYS = ['forward-0', 'forward-1', 'forward-2', 'left-0', 'left-1', 'left-2',
                  'right-0', 'right-1', 'right-2', 'crash', 'crash-burst'];
  var CAR = {};
  CAR_KEYS.forEach(function (k) { CAR[k] = getImg(carUrl(k)); });   // eager preload

  // NPC traffic vehicles — two cars (red jeep, blue sedan), each normal + hit.
  function trafUrl(key) {
    var m = manifest();
    if (m && m.traffic && m.traffic[key]) return m.traffic[key];
    return SCRIPT_BASE ? (SCRIPT_BASE + 'dw-npc-' + key + '.png') : '';
  }
  var VEH = ['jeep', 'sedan'];
  var TRAFFIC = {};
  ['jeep', 'jeep-hit', 'sedan', 'sedan-hit'].forEach(function (k) { TRAFFIC[k] = getImg(trafUrl(k)); });

  // Optional drop-in art for road / scenery / obstacle sprites.
  function bgImg(key) { var m = manifest(); return (m && m[key]) ? getImg(m[key]) : null; }
  function obstImg(type) { var m = manifest(); return (m && m.obstacles && m.obstacles[type]) ? getImg(m.obstacles[type]) : null; }
  function ready(im) { return !!(im && im._ready); }

  function create(opts) {
    opts = opts || {};
    var onEnd = opts.onEnd || function () {};
    var onScore = opts.onScore || function () {};
    var onState = opts.onState || function () {};

    var aspect = (opts.aspect && opts.aspect > 0) ? opts.aspect : 1.2;
    var W = 560, H = Math.max(Math.round(W / aspect), 360);
    var canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    var ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'low';    // downscaling the 944px car art every frame at 'high' is a heavy CPU cost on weak (Intel UHD) hosts; 'low' is far cheaper and barely visible at cabinet size

    var marginX = Math.round(W * 0.12);
    var roadL = marginX, roadR = W - marginX, roadW = roadR - roadL;
    var laneW = roadW / LANES;

    // car geometry — visible body sized to the lane; frame draws bigger so the
    // damage/crash art (which spills past the body) lands correctly around it.
    var BODY_W = Math.min(laneW * 0.58, 60);
    var FRAME_W = BODY_W / BODY_FRAC;
    var FRAME_H = FRAME_W * SPRITE_AR;
    var COLL_W = BODY_W * 0.80;
    var COLL_H = FRAME_H * 0.62;                    // slightly tighter — oncoming closing speed is high, keep dodges fair
    var CAR_CY = H - FRAME_H * 0.5 - 16;            // sprite centre Y
    var CAR_TOP = CAR_CY - COLL_H / 2;              // collision box top

    // NPC sprite frame metrics (constant — used for spawn spacing + the far->near grow)
    var NPC_FW = (laneW * 0.62) / BODY_FRAC;
    var NPC_FH = NPC_FW * SPRITE_AR;

    // static gradients — geometry never changes, so build them once and let the
    // per-frame RAF render reuse them instead of re-allocating every frame.
    var gradGrass = ctx.createLinearGradient(0, 0, 0, H);
    gradGrass.addColorStop(0, C.grass); gradGrass.addColorStop(1, C.grassLt);
    var gradCrown = ctx.createLinearGradient(roadL, 0, roadR, 0);
    gradCrown.addColorStop(0, 'rgba(0,0,0,0.22)'); gradCrown.addColorStop(0.5, 'rgba(255,255,255,0.03)'); gradCrown.addColorStop(1, 'rgba(0,0,0,0.22)');
    var gradHorizon = ctx.createLinearGradient(0, 0, 0, H * 0.34);   // depth haze at the top (where oncoming cars appear)
    gradHorizon.addColorStop(0, 'rgba(31,18,56,0.55)'); gradHorizon.addColorStop(1, 'rgba(31,18,56,0)');
    var gradVig = ctx.createLinearGradient(0, 0, 0, H);
    gradVig.addColorStop(0, 'rgba(31,18,56,0.5)'); gradVig.addColorStop(0.16, 'rgba(31,18,56,0)');
    gradVig.addColorStop(0.84, 'rgba(31,18,56,0)'); gradVig.addColorStop(1, 'rgba(31,18,56,0.55)');
    var gradHead = ctx.createRadialGradient(0, 0, 4, 0, 0, FRAME_W * 0.9);  // built at origin; positioned per frame
    gradHead.addColorStop(0, 'rgba(255,232,166,0.32)'); gradHead.addColorStop(1, 'rgba(255,232,166,0)');

    var van, obstacles, particles, skids, popups, scenery;
    var dist, score, bonus, speed, lives, state, scrollY, spawnCd;
    var steerInput, invuln, shake, pickups, combo, comboT, hitFlash, crashT, mph, skidGap, crashOffset, crashGlideV;
    var demoMode = false, demoT = 0, demoAimX = W / 2;   // attract self-play: AI flag, elapsed-run timer, current target X
    var demoRecycle = false;                         // deferred "recycle the demo run next frame" request (set mid-loop, acted on at the top of update)

    function setState(s) { if (state !== s) { state = s; onState(s); } }
    function laneX(i) { return roadL + (i + 0.5) * laneW; }

    // perspective: an oncoming car is small at the top (far) and full size as it
    // reaches the player line — this single cue does most of the "rushing at you" work.
    function pscale(y) { return clamp(0.6 + 0.44 * (y / CAR_TOP), 0.6, 1.04); }

    function reset() {
      van = { x: W / 2, lean: 0, tilt: 0 };
      obstacles = []; particles = []; skids = []; popups = [];
      dist = 0; score = 0; bonus = 0; speed = BASE_SPEED; lives = START_LIVES;
      scrollY = 0; spawnCd = 0.7; steerInput = 0; invuln = 1.1; shake = 0;
      pickups = 0; combo = 0; comboT = 0; hitFlash = 0; crashT = 0; mph = 0; skidGap = 0; crashOffset = 0; crashGlideV = 0;
      demoT = 0; demoAimX = van.x; demoRecycle = false;
      buildScenery();
      setState('attract'); onScore(0); render();
    }
    function start() { demoMode = false; reset(); setState('playing'); invuln = 1.1; }
    function stop() { demoMode = false; setState('attract'); }

    // Self-playing attract loop: a real 'playing' run the internal AI drives. Never
    // calls onEnd; on any would-be game-over (or after a safety window) it silently
    // re-inits and keeps looping forever. (Public method is demo(); the internal
    // flag is demoMode so they don't collide in this scope.)
    function demo() { demoMode = false; reset(); demoMode = true; setState('playing'); invuln = 1.1; demoT = 0; }
    function restartDemo() { reset(); demoMode = true; setState('playing'); invuln = 1.1; demoT = 0; }

    // While the AI is driving (demo) ignore all external input so stray host
    // events can't fight the attract loop. The AI sets steerInput internally.
    function steer(v) { if (demoMode) return; steerInput = clamp(v || 0, -1, 1); }
    function setDir(d) { if (demoMode) return; steerInput = d === 'left' ? -1 : (d === 'right' ? 1 : 0); }
    function press() {}

    // roadside markers (light poles) recycled down both shoulders for speed cue
    function buildScenery() {
      scenery = [];
      for (var i = 0; i < 7; i++) scenery.push({ y: i * (H / 3) - H, side: -1 });
      for (var j = 0; j < 7; j++) scenery.push({ y: j * (H / 3) - H + H / 6, side: 1 });
    }

    /* ---- spawning ---- */
    function laneAt(x) { return clamp(Math.floor((x - roadL) / laneW), 0, LANES - 1); }

    function spawn() {
      // never block more than 2 of 4 lanes in a wave -> always a clean gap to thread
      var n = (dist > 450 && Math.random() < 0.5) ? 2 : 1;
      var used = {}, busy = {};
      // a lane is "busy" if it still has a car near the top — guarantees a car-length
      // gap between consecutive cars in the same lane so they can never spawn-clip
      var safeGap = NPC_FH * 1.7;
      for (var t = 0; t < obstacles.length; t++) {
        var ob = obstacles[t];
        if (ob.type === 'car' && !ob.hit && ob.y < safeGap) busy[laneAt(ob.x)] = 1;
      }
      for (var k = 0; k < n; k++) {
        var lane, guard = 0;
        do { lane = Math.floor(Math.random() * LANES); guard++; } while ((used[lane] || busy[lane]) && guard < 8);
        if (used[lane] || busy[lane]) break;
        used[lane] = 1;
        var o = { x: laneX(lane), lane: lane, type: 'car', credit: false };
        o.w = laneW * 0.62;
        o.fw = NPC_FW; o.fh = NPC_FH;                 // sprite frame (body ~60% of it; debris spills past)
        o.h = o.fh * 0.66;                            // forgiving collision footprint
        o.y = -o.fh;
        o.rel = rand(0.5, 0.72);                      // its own forward speed -> closes on you at speed*(1+rel)
        o.drift = (Math.random() < 0.28) ? (Math.random() < 0.5 ? -1 : 1) : 0;
        o.veh = VEH[Math.floor(Math.random() * VEH.length)];
        o.col = Math.random() < 0.5 ? C.traffic1 : C.traffic2;  // vector-fallback colour
        o.spinA = 0;
        obstacles.push(o);
      }
      // wax pickup in an open lane (rides the road surface, not oncoming)
      if (Math.random() < 0.5) {
        var pl, g2 = 0; do { pl = Math.floor(Math.random() * LANES); g2++; } while (used[pl] && g2 < 6);
        if (!used[pl]) obstacles.push({ x: laneX(pl), lane: pl, y: -40, w: 30, h: 40, type: 'wax', rel: 1, credit: false });
      }
    }

    // NPC cars are solid to each other — separate any overlap so they can't clip/merge.
    // All oncoming traffic moves the same way (down), so a faster car that catches a
    // slower one ahead just tailgates it bumper-to-bumper instead of passing through.
    function separateCars() {
      for (var a = 0; a < obstacles.length; a++) {
        var A = obstacles[a];
        if (A.type !== 'car' || A.hit) continue;
        for (var b = a + 1; b < obstacles.length; b++) {
          var B = obstacles[b];
          if (B.type !== 'car' || B.hit) continue;
          var dx = B.x - A.x, dy = B.y - A.y;
          var px = (A.w + B.w) / 2 - Math.abs(dx);   // horizontal overlap
          var py = (A.h + B.h) / 2 - Math.abs(dy);   // vertical overlap
          if (px <= 0 || py <= 0) continue;          // not touching
          if (py <= px) {
            // same lane: hold a hard bumper gap — the upper (further) car can't pass through
            var gapY = (A.h + B.h) / 2 + 1;
            if (A.y <= B.y) A.y = B.y - gapY; else B.y = A.y - gapY;
          } else {
            // side-by-side (a drift merged them): shove apart and kill their drift
            var s = dx >= 0 ? 1 : -1, push = px / 2 + 0.5;
            A.x -= s * push; B.x += s * push; A.drift = 0; B.drift = 0;
            A.x = clamp(A.x, roadL + A.w / 2, roadR - A.w / 2);
            B.x = clamp(B.x, roadL + B.w / 2, roadR - B.w / 2);
          }
        }
      }
    }

    /* ---- particles / fx ---- */
    function burst(x, y, n, col, spd, life) {
      for (var i = 0; i < n && particles.length < 90; i++) {
        var a = rand(0, TAU), s = rand(spd * 0.3, spd);
        particles.push({ x: x, y: y, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 30,
          r: rand(2, 5), life: life || 0.7, t: 0, col: col });
      }
    }
    // chunky car-part debris: flung up/out, then gravity drags it down to settle
    function debris(x, y, n) {
      var cols = [C.green, C.greenDk, C.traffic1, C.glass, '#2E3138'];
      for (var i = 0; i < n && particles.length < 90; i++) {
        var a = -Math.PI / 2 + rand(-1.1, 1.1), s = rand(120, 320);
        particles.push({ x: x + rand(-12, 12), y: y + rand(-20, 10),
          vx: Math.cos(a) * s, vy: Math.sin(a) * s - rand(40, 140),
          r: rand(4, 8), life: rand(0.9, 1.5), t: 0, col: cols[i % cols.length] });
      }
    }
    function popup(x, y, txt, col) {
      if (popups.length > 6) popups.shift();
      popups.push({ x: x, y: y, txt: txt, col: col, t: 0, life: 1.0 });
    }
    function addSkid(dt) {
      if (Math.abs(van.lean) < 0.55) { skidGap = 99; return; }   // primed to lay rubber on the next hard turn
      skidGap += speed * dt;
      if (skidGap < 14) return;                                  // throttle: one pair per ~14px travelled, not per frame
      skidGap = 0;
      var off = BODY_W * 0.32, sy = CAR_CY + FRAME_H * 0.28;
      skids.push({ x: van.x - off, y: sy, t: 0 });
      skids.push({ x: van.x + off, y: sy, t: 0 });
      if (skids.length > 60) skids.splice(0, skids.length - 60);
    }

    /* ---- update ---- */
    function update(dt) {
      dt = Math.min(dt, 0.05);

      if (state === 'crashing') { stepCrash(dt); render(); return false; }
      // 'playing' is the only live-ticking state; demo reuses it, just with the AI
      // driving and the loss path neutralised. Everything below runs identically.
      var live = (state === 'playing');
      if (!live) { render(); return false; }

      // safety net: an attract run can never get visually stuck — recycle on a
      // deferred loss request, or after ~25s of continuous play
      if (demoMode) {
        demoT += dt;
        if (demoRecycle || demoT >= 25) { restartDemo(); render(); return true; }
        autoSteer();   // AI computes + applies this frame's steering input
      }

      speed = Math.min(MAX_SPEED, BASE_SPEED + dist * 0.08);
      mph += ((speed * 0.30) - mph) * 0.1;
      scrollY += speed * dt;
      dist += (speed / 40) * dt;

      if (invuln > 0) invuln -= dt;
      if (shake > 0) shake = Math.max(0, shake - dt * 3.2);
      if (hitFlash > 0) hitFlash = Math.max(0, hitFlash - dt * 2.5);
      if (comboT > 0) { comboT -= dt; if (comboT <= 0) combo = 0; }

      // steer: smooth lean toward input, then position (snappier than before)
      van.lean += (steerInput - van.lean) * Math.min(1, dt * 14);
      van.tilt += (van.lean * 0.12 - van.tilt) * 0.3;
      van.x = clamp(van.x + van.lean * STEER_SPEED * dt, roadL + COLL_W / 2, roadR - COLL_W / 2);
      addSkid(dt);

      // scenery scroll
      for (var s = 0; s < scenery.length; s++) {
        scenery[s].y += speed * dt;
        if (scenery[s].y > H + 20) scenery[s].y -= (H + 40);
      }

      // spawn
      spawnCd -= dt;
      if (spawnCd <= 0) { spawn(); spawnCd = clamp(1.05 - dist * 0.0006, 0.45, 1.05); }

      // move + cull
      for (var i = obstacles.length - 1; i >= 0; i--) {
        var o = obstacles[i];
        if (o.hit) {
          // knocked wreck: flies by its own velocity, spins out, recedes off-screen
          o.x += o.vx * dt; o.y += o.vy * dt;
          o.vx *= 0.95; o.vy += 120 * dt;
          o.spin *= 0.98; o.spinA += o.spin * dt;
        } else if (o.type === 'car') {
          o.y += speed * (1 + o.rel) * dt;            // ONCOMING: ground speed + its own speed -> it rushes the player
          if (o.drift) {
            o.x += o.drift * 24 * dt;
            var lo = roadL + o.w / 2, hi = roadR - o.w / 2;
            if (o.x < lo) { o.x = lo; o.drift = 1; } else if (o.x > hi) { o.x = hi; o.drift = -1; }
          }
        } else {
          o.y += speed * dt;                          // wax / props ride the road surface
        }
        if (o.y - o.h / 2 > H + 4) obstacles.splice(i, 1);   // everything exits the bottom
      }

      separateCars();   // NPC cars block each other (no clipping)

      // player collisions + near-miss (only cars + wax remain)
      var box = { x: van.x - COLL_W / 2, y: CAR_TOP, w: COLL_W, h: COLL_H };
      for (var c = obstacles.length - 1; c >= 0; c--) {
        var ob2 = obstacles[c];
        if (ob2.hit) continue;   // a wreck no longer collides or scores
        if (hit(box, ob2)) {
          if (ob2.type === 'wax') { grabWax(ob2); obstacles.splice(c, 1); continue; }
          if (invuln <= 0) { knockCar(ob2); playerHit(ob2); }   // ram a car -> it wrecks, you take damage
          continue;
        }
        // near-miss credit for traffic you thread past cleanly
        if (ob2.type === 'car' && !ob2.credit && ob2.y > CAR_TOP - ob2.h && ob2.y - ob2.h < CAR_TOP + COLL_H) {
          var gap = Math.abs(ob2.x - van.x) - (ob2.w + COLL_W) / 2;
          if (gap > 0 && gap < 16) { ob2.credit = true; nearMiss(ob2); }
        }
      }

      stepFx(dt);
      score = Math.floor(dist) + bonus; onScore(score);
      render();
      return true;
    }

    function stepFx(dt) {
      for (var i = particles.length - 1; i >= 0; i--) {
        var p = particles[i]; p.t += dt;
        p.x += p.vx * dt; p.y += (p.vy + speed * 0.4) * dt; p.vy += 240 * dt;
        if (p.t >= p.life) particles.splice(i, 1);
      }
      for (var j = skids.length - 1; j >= 0; j--) { var k = skids[j]; k.y += speed * dt; k.t += dt; if (k.y > H + 10 || k.t > 1.4) skids.splice(j, 1); }
      for (var u = popups.length - 1; u >= 0; u--) { var q = popups[u]; q.t += dt; q.y -= 38 * dt; if (q.t >= q.life) popups.splice(u, 1); }
    }

    function hit(a, o) {
      var ox = o.x - o.w / 2, oy = o.y - o.h / 2;
      return a.x < ox + o.w && a.x + a.w > ox && a.y < oy + o.h && a.y + a.h > oy;
    }

    /* ---- attract AI ---- greedy lane-picker that reads the same private obstacle
       list a human reads on screen: score each lane by how far ahead its nearest
       threat is (bigger gap = safer), nudge toward wax when the picked lane is
       clean, then steer smoothly toward that lane's centre. Traffic is capped so a
       clear lane always exists, and the lookahead scales with the closing speed so
       it reacts to fast oncoming cars in time — a skilled, unflustered driver. */
    function autoSteer() {
      var carLane = laneAt(van.x);
      var lookahead = COLL_H * (1.6 + speed / 260);   // react sooner the faster traffic closes
      // nearest threatening (un-hit) car ahead in each lane -> clearance distance
      var clear = [];
      for (var l = 0; l < LANES; l++) clear[l] = H * 2;   // empty lane = wide open
      var waxLane = -1, waxGap = 1e9;
      for (var i = 0; i < obstacles.length; i++) {
        var o = obstacles[i];
        if (o.hit) continue;
        var lane = (o.lane != null) ? o.lane : laneAt(o.x);
        if (lane < 0 || lane >= LANES) continue;
        // distance from the player's nose up the road to this object (only count
        // things still ahead of / level with the car — passed traffic is harmless)
        var ahead = (CAR_TOP + COLL_H) - (o.y + o.h / 2);
        if (o.type === 'car') {
          if (ahead > -COLL_H && ahead < clear[lane]) clear[lane] = ahead < 0 ? 0 : ahead;
        } else if (o.type === 'wax') {
          if (ahead > 0 && ahead < waxGap) { waxGap = ahead; waxLane = lane; }
        }
      }
      // pick the lane with the most clearance, biased toward staying put / adjacent
      // so the car doesn't dart across the whole road for a marginal gain
      var best = carLane, bestScore = -1e9;
      for (var c = 0; c < LANES; c++) {
        var sc = clear[c] - Math.abs(c - carLane) * 46;   // travel penalty per lane
        if (clear[c] < lookahead) sc -= 4000;             // hard avoid: threat closing on us
        if (sc > bestScore) { bestScore = sc; best = c; }
      }
      // if the chosen lane is comfortably clear, drift to scoop a nearby wax pickup
      if (waxLane >= 0 && clear[waxLane] > lookahead * 2.4 && Math.abs(waxLane - carLane) <= 1) best = waxLane;

      // steer smoothly toward the target lane centre (proportional, clamped to axis)
      demoAimX = laneX(best);
      var err = demoAimX - van.x;
      steerInput = clamp(err / (laneW * 0.5), -1, 1);
    }

    function grabWax(o) {
      pickups++; combo++; comboT = 3.2;
      var add = 30 + Math.max(0, combo - 1) * 10;
      bonus += add;
      burst(o.x, o.y, 12, C.wax, 160, 0.6);
      popup(o.x, CAR_TOP - 6, '+' + add + (combo > 1 ? '  x' + combo : ''), C.wax);
    }
    function nearMiss(o) {
      combo++; comboT = 3.2;
      var add = 15 + Math.max(0, combo - 1) * 8;
      bonus += add;
      burst(van.x, CAR_TOP, 6, C.spark, 130, 0.4);
      popup(van.x, CAR_TOP - 10, 'NICE +' + add, C.green);
    }
    function playerHit(o) {
      lives--; combo = 0; invuln = 1.4; hitFlash = 1; shake = 1; speed = Math.max(260, speed * 0.7);
      burst((o.x + van.x) / 2, (o.y + CAR_CY) / 2, 14, C.spark, 200, 0.6);
      if (lives <= 0) {
        lives = 0;
        // Demo must never end: instead of crashing (which would fire onEnd at the
        // end of the wreck), flag a silent recycle handled at the top of the next
        // update() — never mutate the obstacle list mid-collision-loop.
        if (demoMode) { demoRecycle = true; return; }
        beginCrash();
      }
      else popup(van.x, CAR_TOP - 8, '-1', C.urgent);
    }
    // Ram an NPC: it switches to its 'hit' sprite, spins out away from you, sheds
    // parts, and recedes off-screen as you blow past it.
    function knockCar(o) {
      o.hit = true; o.credit = true;
      var dir = (o.x >= van.x) ? 1 : -1;
      o.vx = dir * rand(80, 170);
      o.vy = speed * 0.85 + rand(40, 120);   // shoved back; recedes down past you
      o.spin = dir * rand(2.2, 4.2);
      o.spinA = o.spinA || 0;
      debris(o.x, o.y, 12);
      burst(o.x, o.y, 10, C.spark, 180, 0.5);
    }

    function beginCrash() {
      setState('crashing'); crashT = 0; shake = 1.4;
      crashOffset = 0; crashGlideV = Math.max(150, speed * 0.9);   // forward momentum carries the wreck up the road
      speed = 0;                                                   // freeze the world (camera pauses) — only the car glides
      burst(van.x, CAR_CY, 26, C.spark, 260, 0.9);
      burst(van.x, CAR_CY, 18, C.urgent, 200, 0.8);
      debris(van.x, CAR_CY, 16);                                   // car parts fly off and tumble to the ground
    }
    // The host keeps ticking update() during 'over' too (it runs while the cabinet
    // is locked, not only while playing), so onEnd fires only AFTER the wreck has
    // fully played — and the WRECKED frame is painted in this same tick (render()
    // runs right after, in update's non-playing branch), so it's always visible.
    function stepCrash(dt) {
      crashT += dt;
      crashGlideV = Math.max(0, crashGlideV - 360 * dt);   // friction grinds the wreck to a halt
      crashOffset += crashGlideV * dt;                     // world is paused; the car slides forward (up the road)
      if (shake > 0) shake = Math.max(0, shake - dt * 1.5);
      stepFx(dt);
      if (crashT >= CRASH_DUR) { setState('over'); onEnd({ score: score, won: false, lives: 0 }); }
    }

    /* ---- render ---- */
    function render() {
      var sx = shake > 0 ? (rand(-1, 1)) * 4 * shake : 0;
      var sy = shake > 0 ? (rand(-1, 1)) * 3 * shake : 0;
      ctx.save(); ctx.translate(sx, sy);

      drawBackground();
      drawRoad();
      drawSkids();
      // paint far -> near so nearer (larger) oncoming cars overlap further ones
      obstacles.sort(function (a, b) { return a.y - b.y; });
      obstacles.forEach(drawObstacle);
      drawCar();
      drawParticles();
      drawPopups();
      drawSpeedLines();
      drawHitFlash();
      drawVignette();

      ctx.restore();

      drawHud();
      if (state === 'attract') banner('DETAIL DASH', C.green, 'STEER TO PLAY · GRAB WAX · DODGE TRAFFIC');
      else if (state === 'over') banner('WRECKED', C.urgent, 'DROVE ' + Math.floor(dist) + ' M · WAX ' + pickups);
      else if (demoMode) drawDemoTag();
    }

    // unobtrusive attract-mode label while the AI self-plays (banner() would cover
    // the action); blinks gently so it reads as a marquee, not a paused frame
    function drawDemoTag() {
      if (Math.floor(scrollY / 26) % 4 === 0) return;   // soft blink
      ctx.save();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = C.wax; ctx.font = '800 15px Outfit, Arial';
      ctx.fillText('DEMO', W / 2, 50);
      ctx.fillStyle = C.dim; ctx.font = '700 10px DM Sans, Arial';
      ctx.fillText('INSERT TOKEN TO PLAY', W / 2, 66);
      ctx.restore();
    }

    function drawBackground() {
      var road = bgImg('road');
      if (ready(road)) {
        // tile a supplied road texture vertically (scrolls)
        var iw = roadW, ih = iw * (road.naturalHeight / road.naturalWidth);
        var off = scrollY % ih;
        ctx.fillStyle = C.grass; ctx.fillRect(-4, 0, W + 8, H);
        for (var y = off - ih; y < H; y += ih) ctx.drawImage(road, roadL, y, iw, ih);
        return;
      }
      ctx.fillStyle = gradGrass; ctx.fillRect(-4, 0, W + 8, H);
      // scrolling shoulder ticks
      ctx.fillStyle = C.grassLt;
      var tick = (scrollY * 1.0) % 46;
      for (var ty = -46 + tick; ty < H; ty += 46) {
        ctx.fillRect(roadL - 16, ty, 8, 22);
        ctx.fillRect(roadR + 8, ty, 8, 22);
      }
      // light poles
      for (var s = 0; s < scenery.length; s++) {
        var sc = scenery[s], px = sc.side < 0 ? roadL - 26 : roadR + 18;
        ctx.fillStyle = '#0A1F10'; ctx.fillRect(px, sc.y, 5, 26);
        ctx.fillStyle = C.head; ctx.globalAlpha = 0.9; ctx.fillRect(px - 2, sc.y - 3, 9, 5); ctx.globalAlpha = 1;
      }
    }

    function drawRoad() {
      if (!ready(bgImg('road'))) {
        ctx.fillStyle = C.road; ctx.fillRect(roadL, 0, roadW, H);
        // faint asphalt seams (smooth scroll)
        ctx.fillStyle = C.seam;
        var seam = scrollY % 130;
        for (var y = -130 + seam; y < H; y += 130) ctx.fillRect(roadL, y, roadW, 2);
        // subtle crown shading
        ctx.fillStyle = gradCrown; ctx.fillRect(roadL, 0, roadW, H);
      }
      // calm shoulder curbs — solid dark, no strobe (replaces the old flashing rumble)
      ctx.fillStyle = C.curb; ctx.fillRect(roadL - 7, 0, 7, H); ctx.fillRect(roadR, 0, 7, H);
      // crisp thin inner edge lines
      ctx.fillStyle = C.edge; ctx.fillRect(roadL, 0, 2, H); ctx.fillRect(roadR - 2, 0, 2, H);
      // single-colour amber reflectors on the curb — they scroll smoothly (speed cue
      // WITHOUT the jarring two-colour flicker the rumble strip had)
      ctx.fillStyle = C.reflector;
      var rGap = 62, rOff = scrollY % rGap;
      for (var ry = -rGap + rOff; ry < H; ry += rGap) {
        ctx.fillRect(roadL - 6, ry, 4, 11);
        ctx.fillRect(roadR + 2, ry, 4, 11);
      }
      // dashed gold lane dividers (smooth scroll)
      ctx.strokeStyle = C.dash; ctx.lineWidth = 4; ctx.setLineDash([28, 26]);
      ctx.lineDashOffset = -(scrollY % 54);
      for (var l = 1; l < LANES; l++) { var lx = roadL + l * laneW; ctx.beginPath(); ctx.moveTo(lx, 0); ctx.lineTo(lx, H); ctx.stroke(); }
      ctx.setLineDash([]);
    }

    function drawSkids() {
      for (var i = 0; i < skids.length; i++) {
        var k = skids[i];
        ctx.fillStyle = 'rgba(10,8,14,' + (0.34 * (1 - k.t / 1.4)).toFixed(3) + ')';
        ctx.fillRect(k.x - 2, k.y, 4, 9);
      }
    }

    function drawObstacle(o) {
      var im = obstImg(o.type);
      if (ready(im)) {
        var ws = (o.type === 'car') ? pscale(o.y) : 1;
        ctx.drawImage(im, o.x - (o.w * ws) / 2, o.y - (o.h * ws) / 2, o.w * ws, o.h * ws);
        return;
      }
      if (o.type === 'wax') {
        var x = o.x - o.w / 2, y = o.y - o.h / 2;
        ctx.globalAlpha = 0.32; ctx.fillStyle = C.wax; ctx.fillRect(x - 1, y + 5, o.w + 2, o.h - 4); ctx.globalAlpha = 1; // cheap glow (no shadowBlur)
        ctx.fillStyle = C.wax; ctx.fillRect(x + 4, y + 9, o.w - 8, o.h - 11);
        ctx.fillStyle = C.waxDk; ctx.fillRect(o.x - 4, y, 8, 11);
        ctx.fillStyle = C.waxDk; ctx.font = '800 14px Outfit, Arial'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('$', o.x, y + o.h / 2 + 3);
        return;
      }
      // traffic car — real oncoming NPC sprite (jeep/sedan, normal or hit); vector fallback until art loads
      if (drawNpcSprite(o)) return;
      drawNpcVector(o);
    }

    function drawCar() {
      // crash frames take over once the wreck starts
      if (state === 'crashing' || state === 'over') {
        // Camera paused; the wreck has glided forward. Show the clean wreck while it's
        // still sliding, then settle on the debris frame (parts strewn on the ground),
        // which lingers through the game-over screen.
        var resting = state === 'over' || crashGlideV <= 0;
        var ck = resting ? 'crash-burst' : 'crash';
        if (!drawSprite(ck, van.x, CAR_CY - crashOffset, 0)) drawVanVector(true);
        return;
      }
      if (invuln > 0 && state === 'playing' && Math.floor(scrollY / 7) % 2 === 0) return; // blink i-frames
      drawHeadlights();
      var dir = van.lean < -0.22 ? 'left' : (van.lean > 0.22 ? 'right' : 'forward');
      var tier = clamp(START_LIVES - lives, 0, 2);
      if (!drawSprite(dir + '-' + tier, van.x, CAR_CY, van.tilt)) drawVanVector(false);
    }

    function drawSprite(key, cx, cy, tilt) {
      var im = CAR[key];
      if (!ready(im)) return false;
      ctx.save(); ctx.translate(cx, cy); if (tilt) ctx.rotate(tilt);
      ctx.scale(1, -1);   // art faces down the frame; flip vertically so the driver heads UP the road (keeps L/R lean)
      ctx.drawImage(im, -FRAME_W / 2, -FRAME_H / 2, FRAME_W, FRAME_H);
      ctx.restore();
      return true;
    }

    // Oncoming NPC vehicle — jeep/sedan, normal or 'hit'. Drawn UNFLIPPED so the
    // source art (front-of-car at the bottom) faces straight down the road at the
    // player, and grown by perspective so it rushes up at you. Spins if knocked.
    function drawNpcSprite(o) {
      var im = TRAFFIC[o.veh + (o.hit ? '-hit' : '')];
      if (!ready(im)) return false;
      var sc = pscale(o.y);
      ctx.save();
      ctx.translate(o.x, o.y);
      if (o.spinA) ctx.rotate(o.spinA);
      ctx.scale(sc, sc);
      ctx.drawImage(im, -o.fw / 2, -o.fh / 2, o.fw, o.fh);
      ctx.restore();
      if (!o.hit) drawNpcHeadlights(o, sc);   // headlights aimed at the player
      return true;
    }

    // Two soft headlight glows on the front (bottom) of an oncoming car — cheap
    // ellipse fills (no shadowBlur), the "two lights bearing down on you" night cue.
    function drawNpcHeadlights(o, sc) {
      var hh = o.fh * sc, hw = o.fw * sc;
      var fy = o.y + hh * 0.40, off = hw * 0.22, rx = 5 * sc, rynow = 8 * sc;
      ctx.save();
      ctx.globalAlpha = 0.55; ctx.fillStyle = C.head;
      ctx.beginPath(); ctx.ellipse(o.x - off, fy, rx, rynow, 0, 0, TAU); ctx.fill();
      ctx.beginPath(); ctx.ellipse(o.x + off, fy, rx, rynow, 0, 0, TAU); ctx.fill();
      ctx.restore();
    }

    // vector NPC fallback (used only until the sprite art loads) — also oncoming:
    // headlights toward the player at the bottom, taillights at the top, perspective-scaled.
    function drawNpcVector(o) {
      var sc = pscale(o.y), w = o.w, h = o.h;
      ctx.save(); ctx.translate(o.x, o.y); if (o.spinA) ctx.rotate(o.spinA); ctx.scale(sc, sc);
      ctx.fillStyle = 'rgba(0,0,0,0.3)'; ctx.fillRect(-w / 2 + 3, -h / 2 + 6, w, h);
      ctx.fillStyle = o.col; ctx.fillRect(-w / 2, -h / 2, w, h);
      ctx.fillStyle = 'rgba(0,0,0,0.4)'; ctx.fillRect(-w / 2 + 4, -h * 0.30, w - 8, h * 0.2); ctx.fillRect(-w / 2 + 4, h * 0.10, w - 8, h * 0.22);
      ctx.fillStyle = C.glass; ctx.fillRect(-w / 2 + 5, h * 0.12, w - 10, h * 0.12);   // windshield near the front
      ctx.fillStyle = C.head; ctx.fillRect(-w / 2 + 3, h / 2 - 6, 6, 4); ctx.fillRect(w / 2 - 9, h / 2 - 6, 6, 4);   // headlights (front, at player)
      ctx.fillStyle = C.urgent; ctx.fillRect(-w / 2 + 3, -h / 2 + 2, 6, 4); ctx.fillRect(w / 2 - 9, -h / 2 + 2, 6, 4); // taillights (rear)
      if (o.hit) { ctx.fillStyle = 'rgba(0,0,0,0.45)'; ctx.fillRect(-w / 2, -h / 2, w, h); }   // darken the wrecked fallback
      ctx.restore();
    }

    function drawHeadlights() {
      var ty = CAR_CY - FRAME_H * 0.42;
      ctx.save(); ctx.translate(van.x, ty); ctx.fillStyle = gradHead;   // cached radial, positioned per frame
      ctx.beginPath(); ctx.ellipse(0, -12, FRAME_W * 0.7, FRAME_H * 0.5, 0, 0, TAU); ctx.fill();
      ctx.restore();
    }

    // vector fallback van (used only until the sprite loads, or on load failure)
    function drawVanVector(wrecked) {
      var w = BODY_W, h = FRAME_H * 0.82;
      ctx.save(); ctx.translate(van.x, CAR_CY); ctx.rotate(van.tilt);
      ctx.fillStyle = 'rgba(0,0,0,0.3)'; ctx.fillRect(-w / 2 + 3, -h / 2 + 6, w, h);
      ctx.fillStyle = wrecked ? C.grime : C.green; ctx.fillRect(-w / 2, -h / 2, w, h);
      ctx.fillStyle = C.greenDk; ctx.fillRect(-w / 2, -h / 2, w, 8);
      ctx.fillStyle = C.glass; ctx.fillRect(-w / 2 + 5, -h / 2 + 12, w - 10, 16);
      ctx.fillStyle = C.head; ctx.fillRect(-w / 2 + 3, -h / 2 + 2, 6, 5); ctx.fillRect(w / 2 - 9, -h / 2 + 2, 6, 5);
      ctx.restore();
    }

    function drawParticles() {
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i], a = 1 - p.t / p.life;
        ctx.globalAlpha = a < 0 ? 0 : a; ctx.fillStyle = p.col;
        ctx.fillRect(p.x - p.r / 2, p.y - p.r / 2, p.r, p.r);
      }
      ctx.globalAlpha = 1;
    }

    function drawPopups() {
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      for (var i = 0; i < popups.length; i++) {
        var q = popups[i], a = 1 - q.t / q.life;
        ctx.globalAlpha = a < 0 ? 0 : a;
        ctx.fillStyle = q.col; ctx.font = '800 17px Outfit, Arial';
        ctx.fillText(q.txt, q.x, q.y);
      }
      ctx.globalAlpha = 1;
    }

    function drawSpeedLines() {
      var t = clamp((speed - 300) / 300, 0, 1);
      if (t <= 0.02) return;
      ctx.strokeStyle = 'rgba(247,248,250,' + (0.14 * t).toFixed(3) + ')'; ctx.lineWidth = 2;
      var n = Math.round(7 * t);
      for (var i = 0; i < n; i++) {
        var lx = roadL + 10 + ((i * 97 + (scrollY * 2) % roadW) % (roadW - 20));
        var ly = (scrollY * (3 + i)) % H, len = 22 + 44 * t;
        ctx.beginPath(); ctx.moveTo(lx, ly); ctx.lineTo(lx, ly + len); ctx.stroke();
      }
    }

    // Red impact flash — its own pass so it shows even at low speed (when the
    // speed lines are off), e.g. a crash early in the run.
    function drawHitFlash() {
      if (hitFlash > 0) { ctx.fillStyle = 'rgba(229,57,70,' + (0.32 * hitFlash).toFixed(3) + ')'; ctx.fillRect(0, 0, W, H); }
    }

    function drawVignette() {
      ctx.fillStyle = gradHorizon; ctx.fillRect(0, 0, W, H * 0.34);   // depth haze where oncoming cars appear
      ctx.fillStyle = gradVig; ctx.fillRect(0, 0, W, H);
    }

    function drawHud() {
      ctx.fillStyle = 'rgba(11,6,24,0.84)'; ctx.fillRect(0, 0, W, 34);
      ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
      ctx.fillStyle = C.dim; ctx.font = '700 11px DM Sans, Arial'; ctx.fillText('DIST', 8, 17);
      ctx.fillStyle = C.green; ctx.font = '800 16px Outfit, Arial'; ctx.fillText(Math.floor(dist) + ' m', 40, 18);
      // speed readout (centre)
      ctx.fillStyle = C.dim; ctx.font = '700 11px DM Sans, Arial'; ctx.textAlign = 'center';
      ctx.fillText(Math.round(mph) + ' MPH', W / 2, 17);
      // combo (when chaining)
      if (combo > 1) {
        ctx.fillStyle = C.wax; ctx.font = '800 14px Outfit, Arial';
        ctx.fillText('COMBO x' + combo, W / 2, H - 16);
      }
      // lives as little green cars
      for (var i = 0; i < lives; i++) { ctx.fillStyle = C.green; ctx.fillRect(W - 18 - i * 17, 8, 11, 18); ctx.fillStyle = C.greenDk; ctx.fillRect(W - 18 - i * 17, 8, 11, 4); }
      ctx.fillStyle = C.wax; ctx.font = '700 12px DM Sans, Arial'; ctx.textAlign = 'right';
      ctx.fillText('WAX ' + pickups, W - 18 - START_LIVES * 17 - 8, 17);
    }

    function banner(big, color, sub) {
      ctx.fillStyle = 'rgba(11,6,24,0.8)'; ctx.fillRect(0, H * 0.34, W, H * 0.3);
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = color; ctx.font = '800 40px Outfit, Arial'; ctx.shadowColor = color; ctx.shadowBlur = 18;
      ctx.fillText(big, W / 2, H * 0.44); ctx.shadowBlur = 0;
      ctx.fillStyle = C.text; ctx.font = '700 13px DM Sans, Arial'; ctx.fillText(sub, W / 2, H * 0.55);
    }

    function drawAttract() { reset(); }
    reset();

    return {
      canvas: canvas, width: W, height: H,
      // touch:'steer-abs' — the car steers toward wherever your thumb is horizontally
      // on the screen (absolute). Desktop uses ◀ ▶ / A D. No on-screen arrows.
      controls: { steer: true, showLives: true, touch: 'steer-abs', buttons: [] },
      tutorial: [
        { m: { g: 'drag', t: 'Slide your thumb across the screen — the car follows' }, d: { k: ['◀', '▶'], t: 'Press ◀ ▶ (or A / D) to steer' } },
        { m: { g: 'tip', t: 'Grab gold wax · thread past traffic to build combo' }, d: { g: 'tip', t: 'Grab gold wax · thread past traffic to build combo' } },
        { m: { g: 'tip', t: 'Go the distance — you have 3 lives' }, d: { g: 'tip', t: 'Go the distance — you have 3 lives' } }
      ],
      start: start, stop: stop, reset: reset, setDir: setDir, press: press, steer: steer, update: update,
      demo: demo,
      drawAttract: drawAttract,
      getScore: function () { return score; },
      getLives: function () { return Math.max(0, lives); },
      getState: function () { return state; }
    };
  }

  window.DwArcadeGames = window.DwArcadeGames || {};
  window.DwArcadeGames.racing = create;
})();
